import { useToast } from '@/components/ui';
import { useAuth } from '@/contexts/auth-context';
import { useConnection } from '@/features/connections/connection-context';
import { firebaseKeys, useDocument, useDocumentsInfinite } from '@/query/firebase';
import { DOCUMENTS_PAGE_SIZE } from '@shared/constants';
import { OrderBy } from '@shared/types';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryBuilder } from '../../query-builder/query-builder-store';
import {
  getDefaultQueryName,
  loadQuery,
} from '../../query-builder/query-storage';
import {
  clearQueryState,
  loadAllQueryStatesForConnection,
  loadTabIds,
  saveTabIds,
} from '../../tabs-storage';
import { FirestoreDocument } from '../../types';
import { updateUrlParams } from '../../utils';
import {
  getAvailableFields,
  getDisplayDocuments,
  getTableSortState,
  hasCollection,
  isInspectorOpen,
  normalizeOrderByForQueryBuilder,
  shouldShowConnectionBannerOnly,
  shouldShowConnectionSetup,
} from './explorer.domain';

export function useExplorer() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { connectionId } = useConnection();
  const { oauthStatus, refreshOAuthStatus } = useAuth();
  const {
    getState: getQueryState,
    clearQuery,
    setDocumentIdPrefix,
    setSingleOrderBy,
    setListOrderBy,
    executeQuery,
    executeQueryWith,
    executeQueryLoadMore,
    clearAll: clearAllQueries,
    hydrateCollection,
    reset: resetQueryBuilder,
    applyQueryToCollection,
  } = useQueryBuilder();

  const collectionNameRaw = searchParams.get('collection');
  const collectionName = collectionNameRaw ? decodeURIComponent(collectionNameRaw) : null;
  const documentId = searchParams.get('doc');

  const [openTabIds, setOpenTabIds] = useState<string[]>([]);
  const [tabsRestored, setTabsRestored] = useState(false);
  const prevConnectionIdRef = useRef<string | null>(null);
  const skipUrlCollectionOnRestoreRef = useRef(false);
  const restoredForConnectionRef = useRef<string | null>(null);
  const prevOpenTabIdsRef = useRef<string[]>([]);
  /** Set when we run the default query for a collection so we can enable the list after user clears */
  const hasRunDefaultQueryForCollectionRef = useRef<string | null>(null);

  const [queryBuilderOpen, setQueryBuilderOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [lastClickedDocument, setLastClickedDocument] = useState<FirestoreDocument | null>(null);

  const queryState = collectionName ? getQueryState(collectionName) : null;
  const listOrderBy = queryState?.listOrderBy ?? null;
  const hasDefaultQuery = !!(
    connectionId &&
    collectionName &&
    getDefaultQueryName(connectionId, collectionName)
  );
  const documentIdPrefix = collectionName ? (queryState?.documentIdPrefix ?? '') : '';
  const queryResults = queryState?.queryResults ?? null;
  const activeQuery = queryState?.activeQuery ?? null;
  const queryOrderBy = queryState?.orderBy ?? [];

  const queryLoading = queryState?.loading ?? false;
  const queryLoadingMore = queryState?.loadingMore ?? false;

  const searchBarDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Search bar only: debounced run when document ID filter changes; query panel does not auto-run
  const setSearchQueryWithDebounce = useCallback(
    (value: string) => {
      if (!collectionName) return;
      setDocumentIdPrefix(collectionName, value);
      if (searchBarDebounceRef.current) {
        clearTimeout(searchBarDebounceRef.current);
        searchBarDebounceRef.current = null;
      }
      const trimmed = value.trim();
      if (!trimmed) {
        clearQuery(collectionName);
        return;
      }
      searchBarDebounceRef.current = setTimeout(() => {
        searchBarDebounceRef.current = null;
        if (connectionId && collectionName) executeQuery(connectionId, collectionName);
      }, 400);
    },
    [collectionName, connectionId, setDocumentIdPrefix, clearQuery, executeQuery]
  );

  // Clear tabs and queries when connection changes
  useEffect(() => {
    const prev = prevConnectionIdRef.current;
    if (connectionId !== prev) {
      prevConnectionIdRef.current = connectionId;
      hasRunDefaultQueryForCollectionRef.current = null;
      if (connectionId == null) {
        setOpenTabIds([]);
        setTabsRestored(true);
        clearAllQueries();
        return;
      }
      skipUrlCollectionOnRestoreRef.current = true;
      setOpenTabIds([]);
      setTabsRestored(false);
      restoredForConnectionRef.current = null;
      clearAllQueries();
      setSearchParams(updateUrlParams(searchParams, { collection: null, doc: null }), {
        replace: true,
      });
    }
  }, [connectionId, clearAllQueries, searchParams, setSearchParams]);

  // Restore tabs from storage for current connection (once per connection)
  useEffect(() => {
    if (!connectionId || tabsRestored) return;
    if (restoredForConnectionRef.current === connectionId) return;
    if (skipUrlCollectionOnRestoreRef.current) {
      skipUrlCollectionOnRestoreRef.current = false;
      setTabsRestored(true);
      return;
    }
    restoredForConnectionRef.current = connectionId;
    setTabsRestored(true);
    const stored = loadTabIds(connectionId);
    if (stored.length > 0) {
      setOpenTabIds(stored);
      const queryStates = loadAllQueryStatesForConnection(connectionId);
      Object.entries(queryStates).forEach(([cid, state]) => hydrateCollection(cid, state));
      const activeFromUrl = collectionName;
      if (!activeFromUrl || !stored.includes(activeFromUrl)) {
        const newParams = updateUrlParams(searchParams, {
          connectionId,
          collection: stored[0],
          doc: null,
        });
        setSearchParams(newParams, { replace: true });
      }
    } else if (collectionName) {
      setOpenTabIds([collectionName]);
      saveTabIds(connectionId, [collectionName]);
    }
  }, [
    connectionId,
    tabsRestored,
    collectionName,
    searchParams,
    setSearchParams,
    hydrateCollection,
  ]);

  // Keep openTabIds in sync with URL when user navigates (e.g. breadcrumb) to a collection not in tabs
  useEffect(() => {
    if (!connectionId || !collectionName) return;
    setOpenTabIds((prev) => {
      if (prev.includes(collectionName)) return prev;
      const next = [...prev, collectionName];
      saveTabIds(connectionId, next);
      return next;
    });
  }, [connectionId, collectionName]);

  // Persist openTabIds when they change (user closed a tab or we restored)
  useEffect(() => {
    if (!connectionId || openTabIds.length === 0) return;
    saveTabIds(connectionId, openTabIds);
  }, [connectionId, openTabIds]);

  // Apply and run default query when user opens a collection (new tab) or when tabs are restored
  useEffect(() => {
    const newlyAdded = openTabIds.filter(
      (id) => !prevOpenTabIdsRef.current.includes(id)
    );
    const isViewingNewOrRestoredTab =
      newlyAdded.length >= 1 &&
      newlyAdded.includes(collectionName ?? '') &&
      connectionId &&
      collectionName;
    if (isViewingNewOrRestoredTab) {
      const defaultName = getDefaultQueryName(connectionId, collectionName);
      if (defaultName) {
        const query = loadQuery(connectionId, collectionName, defaultName);
        if (query) {
          applyQueryToCollection(collectionName, query);
          void executeQueryWith(connectionId, collectionName, query);
        }
      }
    }
    prevOpenTabIdsRef.current = openTabIds;
  }, [
    connectionId,
    collectionName,
    openTabIds,
    applyQueryToCollection,
    executeQueryWith,
  ]);

  // Set ref only after activeQuery is committed so useDocumentsInfinite stays disabled until then
  useEffect(() => {
    if (
      connectionId &&
      collectionName &&
      getDefaultQueryName(connectionId, collectionName) &&
      activeQuery
    ) {
      hasRunDefaultQueryForCollectionRef.current = collectionName;
    }
  }, [connectionId, collectionName, activeQuery]);

  useEffect(() => {
    const connected = searchParams.get('connected');
    const oauthError = searchParams.get('oauth_error');
    const refreshTokenError = searchParams.get('error');

    if (connected === 'google') {
      searchParams.delete('connected');
      setSearchParams(searchParams, { replace: true });
      refreshOAuthStatus();
    }
    if (oauthError) {
      toast({
        title: 'Connection Failed',
        description: decodeURIComponent(oauthError),
        variant: 'destructive',
      });
      searchParams.delete('oauth_error');
      setSearchParams(searchParams, { replace: true });
    }
    if (refreshTokenError === 'refresh_token_missing') {
      toast({
        title: 'Connection Incomplete',
        description:
          'No refresh token received. Please reconnect and ensure you grant offline access.',
        variant: 'destructive',
      });
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, toast, refreshOAuthStatus]);

  const {
    data: documentsInfiniteData,
    isLoading: documentsLoading,
    isFetching: documentsFetching,
    isFetchingNextPage: documentsLoadingMore,
    error: documentsError,
    hasNextPage: documentsHasMore,
    fetchNextPage: documentsLoadMore,
    refetch: documentsReset,
  } = useDocumentsInfinite(connectionId, collectionName, listOrderBy, {
    enabled:
      !!connectionId &&
      !!collectionName &&
      (!hasDefaultQuery ||
        (hasRunDefaultQueryForCollectionRef.current === collectionName && !activeQuery)),
  });

  const documents = useMemo(
    () => (documentsInfiniteData?.pages.flatMap((p) => p.documents) ?? []) as FirestoreDocument[],
    [documentsInfiniteData?.pages]
  );

  const { data: currentDocumentData } = useDocument(connectionId, collectionName, documentId);
  const currentDocument = (currentDocumentData ?? null) as FirestoreDocument | null;

  const selectedDocument = documentId
    ? currentDocument ?? (lastClickedDocument?.id === documentId ? lastClickedDocument : null)
    : null;

  const displayDocuments = useMemo(
    () => getDisplayDocuments(queryResults, null, documents),
    [queryResults, documents]
  );
  const availableFields = useMemo(() => getAvailableFields(displayDocuments), [displayDocuments]);
  const inspectorOpen = isInspectorOpen(documentId, currentDocument, selectedDocument);
  const listOrderByForTable = useMemo(() => {
    if (!listOrderBy) return null;
    return {
      field: listOrderBy.field === '__name__' ? '__id__' : listOrderBy.field,
      direction: listOrderBy.direction,
    };
  }, [listOrderBy]);
  const tableSortState = getTableSortState(queryResults, queryOrderBy, listOrderByForTable);

  const handleSelectCollection = useCallback(
    (collectionId: string) => {
      setOpenTabIds((prev) => {
        const next = prev.includes(collectionId) ? prev : [...prev, collectionId];
        if (connectionId) saveTabIds(connectionId, next);
        return next;
      });
      const newParams = updateUrlParams(searchParams, {
        connectionId: connectionId || undefined,
        collection: collectionId,
        doc: null,
      });
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams, connectionId]
  );

  const handleSwitchTab = useCallback(
    (collectionId: string) => {
      const newParams = updateUrlParams(searchParams, {
        connectionId: connectionId || undefined,
        collection: collectionId,
        doc: null,
      });
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams, connectionId]
  );

  const handleCloseTab = useCallback(
    (collectionId: string) => {
      resetQueryBuilder(collectionId);
      if (connectionId) clearQueryState(connectionId, collectionId);
      if (collectionName === collectionId) {
        const remaining = openTabIds.filter((id) => id !== collectionId);
        const closedIndex = openTabIds.indexOf(collectionId);
        const newActive = remaining[closedIndex - 1] ?? remaining[0];
        if (newActive != null) {
          const newParams = updateUrlParams(searchParams, {
            connectionId: connectionId || undefined,
            collection: newActive,
            doc: null,
          });
          setSearchParams(newParams);
        } else {
          const newParams = updateUrlParams(searchParams, {
            connectionId: connectionId || undefined,
            collection: null,
            doc: null,
          });
          setSearchParams(newParams);
        }
      }
      setOpenTabIds((prev) => {
        const next = prev.filter((id) => id !== collectionId);
        if (connectionId) saveTabIds(connectionId, next);
        return next;
      });
    },
    [collectionName, openTabIds, searchParams, setSearchParams, connectionId, resetQueryBuilder]
  );

  const handleBreadcrumbNavigate = useCallback(
    (path: string | null) => {
      const newParams = updateUrlParams(searchParams, {
        connectionId: connectionId || undefined,
        collection: path,
        doc: null,
      });
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams, connectionId]
  );

  const handleRowClick = useCallback(
    (document: FirestoreDocument) => {
      if (documentId === document.id && selectedDocument?.id === document.id) return;
      setLastClickedDocument(document);
      const newParams = updateUrlParams(searchParams, {
        connectionId: connectionId || undefined,
        collection: collectionName || undefined,
        doc: document.id,
      });
      setSearchParams(newParams);
    },
    [documentId, selectedDocument?.id, searchParams, setSearchParams, connectionId, collectionName]
  );

  const handleViewJson = useCallback(() => setEditorOpen(true), []);

  const handleCloseInspector = useCallback(
    (open: boolean) => {
      if (!open && documentId) {
        const newParams = updateUrlParams(searchParams, {
          connectionId: connectionId || undefined,
          collection: collectionName || undefined,
          doc: null,
        });
        setSearchParams(newParams);
      }
    },
    [documentId, searchParams, setSearchParams, connectionId, collectionName]
  );

  const handleClearQuery = useCallback(() => {
    if (collectionName) {
      clearQuery(collectionName);
      setListOrderBy(collectionName, null);
      if (connectionId) {
        queryClient.invalidateQueries({
          queryKey: firebaseKeys.documentsInfinitePrefix(connectionId, collectionName),
        });
      }
    }
  }, [clearQuery, collectionName, setListOrderBy, connectionId, queryClient]);

  const handleSortChange = useCallback(
    (newSortState: OrderBy | null) => {
      if (!collectionName) return;
      if (newSortState) {
        setSingleOrderBy(collectionName, normalizeOrderByForQueryBuilder(newSortState));
      } else {
        setSingleOrderBy(collectionName, null);
      }
      if (queryResults && activeQuery && connectionId) {
        executeQuery(connectionId, collectionName);
      } else {
        setListOrderBy(
          collectionName,
          newSortState ? normalizeOrderByForQueryBuilder(newSortState) : null
        );
      }
    },
    [
      queryResults,
      activeQuery,
      connectionId,
      collectionName,
      setSingleOrderBy,
      setListOrderBy,
      executeQuery,
    ]
  );

  const handleRefresh = useCallback(() => {
    if (connectionId && collectionName) {
      queryClient.invalidateQueries({
        queryKey: firebaseKeys.documentsInfinitePrefix(connectionId, collectionName),
      });
    }
  }, [connectionId, collectionName, queryClient]);

  const isLoading = documentsLoading || queryLoading;
  const displayError = documentsError?.message ?? null;
  const listPageSize =
    documentsInfiniteData?.pages?.at(-1)?.limit ?? DOCUMENTS_PAGE_SIZE;
  const listHasMore =
    (documentsHasMore ?? false) || documents.length === listPageSize;
  const tableHasMore = queryResults
    ? (queryResults.hasMore ?? false)
    : listHasMore;
  const handleLoadMore = useCallback(() => {
    if (queryResults && connectionId && collectionName) {
      void executeQueryLoadMore(connectionId, collectionName);
    } else {
      documentsLoadMore();
    }
  }, [queryResults, connectionId, collectionName, executeQueryLoadMore, documentsLoadMore]);
  const tableLoadingMore = queryResults ? queryLoadingMore : documentsLoadingMore;
  const showConnectionSetup = shouldShowConnectionSetup(connectionId, oauthStatus);
  const showConnectionBannerOnly = shouldShowConnectionBannerOnly(connectionId, oauthStatus);
  const showCollectionContent = hasCollection(collectionName);

  return {
    // Layout / routing
    showConnectionSetup,
    showConnectionBannerOnly,
    showCollectionContent,
    collectionName,
    connectionId,
    oauthStatus,
    refreshOAuthStatus,

    // UI state
    settingsOpen,
    setSettingsOpen,
    queryBuilderOpen,
    setQueryBuilderOpen,
    searchQuery: documentIdPrefix,
    setSearchQuery: (value: string) => setSearchQueryWithDebounce(value),
    editorOpen,
    setEditorOpen,

    // Data
    displayDocuments,
    displayError,
    isLoading,
    documentsLoadingMore: tableLoadingMore,
    hasMore: tableHasMore,
    documentsLoadMore: handleLoadMore,
    documentsReset,
    documentsFetching,
    queryResults,
    activeQuery,
    queryOrderBy,
    tableSortState,
    availableFields,

    // Document / inspector
    selectedDocument,
    inspectorOpen,
    handleRowClick,
    handleViewJson,
    handleCloseInspector,

    // Tabs
    openTabIds,
    handleSwitchTab,
    handleCloseTab,

    // Navigation
    handleSelectCollection,
    handleBreadcrumbNavigate,
    handleClearQuery,
    handleSortChange,
    handleRefresh,
  };
}
